import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronRight, Copy, MessageSquare, Wrench } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { observabilityService, type RuntimeTimelineEvent } from '@/services/observabilityService'
import { conversationsApi } from '@/services/conversationsApi'
import type { Mail } from '@/lib/data'
import { Button } from '@/components/ui/button'

interface ConversationTraceabilityProps {
  conversation: Mail | null
  lang?: string
}

type TraceMessage = { role: 'user' | 'assistant'; text: string }

type ToolDebug = {
  id: string
  toolCallId?: string
  name: string
  status: 'success' | 'error' | 'started' | 'incomplete'
  latency_ms?: number | null
  requestMethod?: string
  requestUrl?: string
  requestHeaders?: Record<string, unknown> | null
  requestBody?: Record<string, unknown> | null
  requestQueryParams?: Record<string, unknown> | null
  response?: Record<string, unknown> | null
  error?: Record<string, unknown> | null
  extractedParams?: Record<string, unknown> | null
}

type TurnDebug = {
  turn_id: string
  ts: string
  trace_id: string
  messages: TraceMessage[]
  llm?: {
    provider?: string | null
    model?: string | null
    input_tokens?: number | null
    output_tokens?: number | null
    total_tokens?: number | null
    cost_usd_estimate?: number | null
    latency_ms?: number | null
    usage_status?: string | null
    pricing_source?: string | null
  }
  tools: ToolDebug[]
}

type ConversationMessageLike = {
  id?: string
  text?: string | null
  sender?: string
  timestamp?: string
  metadata?: Record<string, unknown> | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function toMs(value: string | null | undefined): number {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatTurnTimestamp(value: string): string {
  const ms = toMs(value)
  if (!ms) return value
  return new Intl.DateTimeFormat('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ms))
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={() => {
        void Promise.resolve(navigator.clipboard?.writeText(value))
          .then(() => {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          })
          .catch(() => undefined)
      }}
      aria-label={label ? undefined : 'Copiar'}
      title="Copiar"
      className="shrink-0 gap-1 font-medium text-muted-foreground hover:text-foreground"
    >
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
      {label ? <span>{copied ? 'Copiado' : label}</span> : null}
    </Button>
  )
}

/** Bloque de datos crudos. Scrollea en su propio eje X para que una linea larga
 *  no pueda ensanchar el panel (era la causa del corte en desktop). */
function JsonBlock({ data }: { data: unknown }) {
  const text = useMemo(() => JSON.stringify(data, null, 2) ?? 'null', [data])
  return (
    <div className="relative min-w-0">
      <pre className="max-h-72 min-w-0 overflow-auto rounded-lg border bg-muted/40 p-3 pr-14 text-[11px] leading-5">
        <code className="whitespace-pre font-mono">{text}</code>
      </pre>
      <div className="absolute right-2 top-2">
        <CopyButton value={text} />
      </div>
    </div>
  )
}

function DataBlock({ title, data, tone = 'default' }: { title: string; data: unknown; tone?: 'default' | 'danger' }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <p className={cn(
        'text-[11px] font-semibold uppercase tracking-wide',
        tone === 'danger' ? 'text-destructive' : 'text-muted-foreground',
      )}>
        {title}
      </p>
      <JsonBlock data={data} />
    </div>
  )
}

/** Los IDs son cadenas sin espacios: sin `break-all` fijan un min-content que
 *  ensancha el panel entero y recorta la barra de pestañas. */
function IdRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <code className="min-w-0 flex-1 break-all rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">{value}</code>
      <CopyButton value={value} />
    </div>
  )
}

function StatCard({ label, value, tone = 'default' }: { label: string; value: string | number; tone?: 'default' | 'danger' }) {
  return (
    <div className={cn(
      'min-w-0 rounded-lg border p-3',
      tone === 'danger' ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card',
    )}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn(
        'mt-1 break-words text-lg font-semibold leading-tight tabular-nums',
        tone === 'danger' && 'text-destructive',
      )}>
        {value}
      </p>
    </div>
  )
}

function StatusPill({ status }: { status: ToolDebug['status'] }) {
  const styles: Record<ToolDebug['status'], { label: string; className: string }> = {
    success: { label: 'Correcta', className: 'border-success/30 bg-success/10 text-success' },
    error: { label: 'Con error', className: 'border-destructive/40 bg-destructive/10 text-destructive' },
    started: { label: 'En curso', className: 'border-primary/30 bg-primary/10 text-primary' },
    incomplete: { label: 'Incompleta', className: 'border-warning/30 bg-warning/10 text-warning' },
  }
  const style = styles[status] ?? styles.incomplete
  return (
    <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold', style.className)}>
      {style.label}
    </span>
  )
}

function EmptyData({ text }: { text: string }) {
  return <p className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground">{text}</p>
}

function hasVisibleData(value: Record<string, unknown> | null | undefined): boolean {
  return !!value && Object.keys(value).length > 0
}

function normalizeMessages(payload: unknown): ConversationMessageLike[] {
  if (Array.isArray(payload)) {
    return payload.filter((item) => !!asRecord(item)).map((item) => item as ConversationMessageLike)
  }
  const obj = asRecord(payload)
  if (!obj) return []
  const results = obj.results
  if (Array.isArray(results)) {
    return results.filter((item) => !!asRecord(item)).map((item) => item as ConversationMessageLike)
  }
  return []
}

function pushUniqueMessage(messages: TraceMessage[], role: 'user' | 'assistant', text: string) {
  const normalized = text.trim()
  if (!normalized) return
  const alreadyExists = messages.some((m) => m.role === role && m.text === normalized)
  if (!alreadyExists) messages.push({ role, text: normalized })
}

function getMessageRuntimeMetadata(message: ConversationMessageLike): Record<string, unknown> | null {
  const metadata = asRecord(message.metadata)
  if (!metadata) return null
  const nestedTurnMetadata = asRecord(metadata.turn_metadata)
  return nestedTurnMetadata || metadata
}

function getMessageTurnId(message: ConversationMessageLike): string | null {
  const runtimeMetadata = getMessageRuntimeMetadata(message)
  return asString(runtimeMetadata?.turn_id)
}

function getMessageTraceId(message: ConversationMessageLike): string | null {
  const runtimeMetadata = getMessageRuntimeMetadata(message)
  return asString(runtimeMetadata?.trace_id)
}

function buildTurnsFromMessages(messages: ConversationMessageLike[]): TurnDebug[] {
  const sorted = [...messages]
    .filter((m) => typeof m.text === 'string' && (m.text || '').trim())
    .sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp))

  const turns: TurnDebug[] = []
  let currentTurn: TurnDebug | null = null

  for (let index = 0; index < sorted.length; index += 1) {
    const msg = sorted[index]
    const sender = (msg.sender || '').toLowerCase()
    const role: 'user' | 'assistant' = sender === 'user' ? 'user' : 'assistant'
    const text = (msg.text || '').trim()
    const realTurnId = getMessageTurnId(msg)
    const realTraceId = getMessageTraceId(msg)
    if (!text) continue

    if (!currentTurn || role === 'user') {
      currentTurn = {
        turn_id: realTurnId || `msg-turn-${msg.id || index + 1}`,
        ts: msg.timestamp || new Date().toISOString(),
        trace_id: realTraceId || '',
        messages: [],
        tools: [],
      }
      turns.push(currentTurn)
    }

    if (realTurnId && currentTurn.turn_id !== realTurnId) {
      currentTurn.turn_id = realTurnId
    }
    if (realTraceId && !currentTurn.trace_id) {
      currentTurn.trace_id = realTraceId
    }

    pushUniqueMessage(currentTurn.messages, role, text)
  }

  return turns
}

function mapEventsToTurns(events: RuntimeTimelineEvent[], baseTurns: TurnDebug[]): TurnDebug[] {
  const grouped = new Map<string, TurnDebug>()
  const toolsByTurn = new Map<string, Map<string, ToolDebug>>()
  const SYNTHETIC_TURN_PREFIX = 'msg-turn-'
  const SYNTHETIC_MATCH_WINDOW_MS = 15_000

  for (const turn of baseTurns) {
    grouped.set(`base:${turn.turn_id}`, turn)
  }

  function rekeyTurn(oldKey: string, newKey: string, turn: TurnDebug) {
    if (oldKey === newKey) return
    grouped.delete(oldKey)
    grouped.set(newKey, turn)
    const existingTools = toolsByTurn.get(oldKey)
    if (existingTools) {
      toolsByTurn.delete(oldKey)
      toolsByTurn.set(newKey, existingTools)
    }
  }

  function toolFingerprint(
    turnKey: string,
    callId: string | null | undefined,
    name: string,
    method: string | null | undefined,
    url: string | null | undefined,
    timestamp: string
  ): string {
    if (callId) {
      return `${turnKey}::call_id::${callId}`
    }
    const bucket = Math.floor(toMs(timestamp) / 5000) // fallback bucket of 5s
    return `${turnKey}::${name}::${(method || '-').toUpperCase()}::${url || '-'}::${bucket}`
  }

  function ensureTurn(event: RuntimeTimelineEvent): { key: string; turn: TurnDebug } {
    const eventTurnId = asString(event.turn_id)
    if (eventTurnId && eventTurnId !== 'turn-unknown') {
      const existing = Array.from(grouped.entries()).find(([, t]) => t.turn_id === eventTurnId)
      if (existing) return { key: existing[0], turn: existing[1] }

      const eventMs = toMs(event.timestamp)
      const syntheticCandidates = Array.from(grouped.entries())
        .filter(([, t]) => t.turn_id.startsWith(SYNTHETIC_TURN_PREFIX))
        .map(([key, turn]) => ({
          key,
          turn,
          diff: Math.abs(eventMs - toMs(turn.ts)),
        }))
        .filter((candidate) => candidate.diff <= SYNTHETIC_MATCH_WINDOW_MS)
        .sort((a, b) => a.diff - b.diff)

      const syntheticMatch = syntheticCandidates[0]
      if (syntheticMatch) {
        syntheticMatch.turn.turn_id = eventTurnId
        if (!syntheticMatch.turn.trace_id && event.trace_id) {
          syntheticMatch.turn.trace_id = event.trace_id
        }
        const newKey = `${event.trace_id || 'trace-unknown'}:${eventTurnId}`
        rekeyTurn(syntheticMatch.key, newKey, syntheticMatch.turn)
        return { key: newKey, turn: syntheticMatch.turn }
      }

      const key = `${event.trace_id || 'trace-unknown'}:${eventTurnId}`
      const turn: TurnDebug = {
        turn_id: eventTurnId,
        ts: event.timestamp,
        trace_id: event.trace_id,
        messages: [],
        tools: [],
      }
      grouped.set(key, turn)
      return { key, turn }
    }

    const allTurns = Array.from(grouped.entries())
    if (allTurns.length > 0) {
      const eventMs = toMs(event.timestamp)
      let best = allTurns[0]
      let bestDiff = Math.abs(eventMs - toMs(best[1].ts))

      for (const candidate of allTurns) {
        const diff = Math.abs(eventMs - toMs(candidate[1].ts))
        if (diff < bestDiff) {
          best = candidate
          bestDiff = diff
        }
      }
      return { key: best[0], turn: best[1] }
    }

    const key = `${event.trace_id || 'trace-unknown'}:turn-unknown`
    const turn: TurnDebug = {
      turn_id: 'turn-unknown',
      ts: event.timestamp,
      trace_id: event.trace_id,
      messages: [],
      tools: [],
    }
    grouped.set(key, turn)
    return { key, turn }
  }

  for (const event of events) {
    const payload = asRecord(event.payload)
    const { key, turn } = ensureTurn(event)

    if (!turn.trace_id && event.trace_id) {
      turn.trace_id = event.trace_id
    }
    if (event.timestamp < turn.ts) {
      turn.ts = event.timestamp
    }

    if (event.event_type === 'llm.request') {
      const questionPreview = asString(payload?.question_preview)
      if (questionPreview) {
        pushUniqueMessage(turn.messages, 'user', questionPreview)
      }
      continue
    }

    if (event.event_type === 'llm.response') {
      turn.llm = {
        provider: event.provider,
        model: event.model,
        input_tokens: event.input_tokens,
        output_tokens: event.output_tokens,
        total_tokens: event.total_tokens,
        cost_usd_estimate: event.cost_usd_estimate,
        latency_ms: event.latency_ms,
        usage_status: event.usage_status,
        pricing_source: event.pricing_source,
      }

      const answerPreview = asString(payload?.response_preview)
      const hasPersistedAssistantMessage = turn.messages.some((message) => message.role === 'assistant')
      if (answerPreview && !hasPersistedAssistantMessage) {
        pushUniqueMessage(turn.messages, 'assistant', answerPreview)
      }
      continue
    }

    if (!event.event_type.startsWith('tool_call.')) {
      continue
    }

    const request = asRecord(payload?.request)
    const requestHeaders = asRecord(request?.headers)
    const requestBody = asRecord(request?.body)
    const requestQueryParams = asRecord(request?.query_params)
    const toolCallId = asString(payload?.tool_call_id)
    // `?? undefined` porque `asString` devuelve string | null y estos dos campos
    // se declaran opcionales (string | undefined). Sin esto no compila.
    const requestMethod = asString(request?.method) || asString(payload?.method) || undefined
    const requestUrl = asString(request?.url) || asString(payload?.url) || undefined

    const extractedParams = asRecord(payload?.llm_extracted_params) || asRecord(payload?.extracted_params)
    const response = asRecord(payload?.response)
      || (payload?.status_code != null ? { status_code: payload.status_code } as Record<string, unknown> : null)
    const error = asRecord(payload?.error)
      || (asString(payload?.error) ? { message: asString(payload?.error) } as Record<string, unknown> : null)

    const toolName = asString(event.tool_name) || 'tool'
    const fp = toolFingerprint(key, toolCallId, toolName, requestMethod, requestUrl, event.timestamp)
    if (!toolsByTurn.has(key)) {
      toolsByTurn.set(key, new Map<string, ToolDebug>())
    }
    const turnRuns = toolsByTurn.get(key)!

    const previous = turnRuns.get(fp)
    if (!previous) {
      const initial: ToolDebug = {
        id: `${event.log_id || event.timestamp}-${turn.tools.length}`,
        toolCallId: toolCallId || undefined,
        name: toolName,
        status:
          event.event_type === 'tool_call.error'
            ? 'error'
            : event.event_type === 'tool_call.finished'
              ? 'success'
              : 'started',
        latency_ms: event.latency_ms,
        requestMethod,
        requestUrl,
        requestHeaders,
        requestBody,
        requestQueryParams,
        response,
        error,
        extractedParams,
      }
      turnRuns.set(fp, initial)
      turn.tools.push(initial)
      continue
    }

    previous.status =
      event.event_type === 'tool_call.error'
        ? 'error'
        : event.event_type === 'tool_call.finished'
          ? 'success'
          : previous.status

    if (event.latency_ms != null) previous.latency_ms = event.latency_ms
    if (toolCallId) previous.toolCallId = toolCallId
    if (requestMethod) previous.requestMethod = requestMethod
    if (requestUrl) previous.requestUrl = requestUrl
    if (requestHeaders) previous.requestHeaders = requestHeaders
    if (requestBody) previous.requestBody = requestBody
    if (requestQueryParams) previous.requestQueryParams = requestQueryParams
    if (response) previous.response = response
    if (error) previous.error = error
    if (extractedParams) previous.extractedParams = extractedParams
  }

  const turns = Array.from(grouped.values()).sort((a, b) => a.ts.localeCompare(b.ts))
  for (const turn of turns) {
    for (const tool of turn.tools) {
      if (tool.status === 'started') {
        tool.status = 'incomplete'
      }
    }
  }
  return turns
}

export function ConversationTraceability({ conversation }: ConversationTraceabilityProps) {
  const [events, setEvents] = useState<RuntimeTimelineEvent[]>([])
  const [messages, setMessages] = useState<ConversationMessageLike[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!conversation?.id) {
      setEvents([])
      setMessages([])
      setError(null)
      return
    }

    let isMounted = true
    setIsLoading(true)
    setError(null)

    Promise.all([
      observabilityService.getTimeline({
        conversation_id: conversation.id,
        limit: 1000,
      }),
      conversationsApi.getConversationMessages(conversation.id, { page_size: 200 }),
    ])
      .then(([timeline, rawMessages]) => {
        if (!isMounted) return
        setEvents(timeline.results)
        setMessages(normalizeMessages(rawMessages))
      })
      .catch((err) => {
        if (!isMounted) return
        setError(err instanceof Error ? err.message : 'No se pudo cargar la trazabilidad.')
        setEvents([])
        setMessages([])
      })
      .finally(() => {
        if (!isMounted) return
        setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [conversation?.id])

  const turns = useMemo(() => {
    if (!conversation) {
      return []
    }

    const baseTurns = buildTurnsFromMessages(messages)
    return mapEventsToTurns(events, baseTurns)
  }, [conversation, events, messages])
  const traceId = turns[0]?.trace_id || events[0]?.trace_id || ''
  const conversationId = conversation?.id ?? ''

  const totals = useMemo(() => {
    const messagesCount = turns.reduce((acc, t) => acc + t.messages.length, 0)
    const tools = turns.reduce((acc, t) => acc + t.tools.length, 0)
    const toolErrors = turns.reduce((acc, t) => acc + t.tools.filter((x) => x.status === 'error').length, 0)
    const totalCost = turns.reduce((acc, t) => acc + (t.llm?.cost_usd_estimate || 0), 0)
    const totalTokens = turns.reduce((acc, t) => acc + (t.llm?.total_tokens || 0), 0)
    const llmLatencies = turns
      .map((t) => t.llm?.latency_ms)
      .filter((value): value is number => typeof value === 'number' && value > 0)
    const avgLatency = llmLatencies.length > 0
      ? Math.round(llmLatencies.reduce((acc, current) => acc + current, 0) / llmLatencies.length)
      : 0
    return { messages: messagesCount, tools, toolErrors, totalCost, totalTokens, avgLatency }
  }, [turns])

  const [expandAll, setExpandAll] = useState(true)

  if (!conversation) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
        Selecciona una conversación para ver qué hizo la IA.
      </div>
    )
  }

  return (
    <div className="flex h-full min-w-0 flex-col bg-background">
      {/* ── Cabecera: resumen en cristiano ───────────────────────────── */}
      <div className="min-w-0 border-b px-4 py-4">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-tight">Qué hizo la IA</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Cada <strong className="font-medium text-foreground">turno</strong> es un mensaje del cliente y la
              respuesta de la IA, con todo lo que consultó por dentro para responder.
            </p>
          </div>
          <Button
            variant="outline"
            size="xs"
            onClick={() => setExpandAll((value) => !value)}
            className="shrink-0 font-medium text-muted-foreground hover:text-foreground"
          >
            {expandAll ? 'Contraer todo' : 'Expandir todo'}
          </Button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 2xl:grid-cols-4">
          <StatCard label="Turnos" value={turns.length} />
          <StatCard label="Mensajes" value={totals.messages} />
          <StatCard label="Herramientas" value={totals.tools} />
          <StatCard
            label="Errores"
            value={totals.toolErrors}
            tone={totals.toolErrors > 0 ? 'danger' : 'default'}
          />
          <StatCard label="Tokens" value={totals.totalTokens.toLocaleString('es-ES')} />
          <StatCard label="Coste total" value={`${totals.totalCost.toFixed(7)} USD`} />
          <StatCard label="Latencia media" value={`${totals.avgLatency} ms`} />
        </div>

        <details className="group mt-3 min-w-0 rounded-lg border bg-muted/20 px-3 py-2">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" />
            Identificadores técnicos
          </summary>
          <div className="mt-2 min-w-0 space-y-1.5">
            <IdRow label="conversation_id" value={conversationId} />
            {traceId ? <IdRow label="trace_id" value={traceId} /> : null}
          </div>
        </details>
      </div>

      {/* ── Línea temporal de turnos ─────────────────────────────────── */}
      <div className="min-h-0 min-w-0 flex-1">
        <ScrollArea className="h-full min-h-0">
          {/* `key` remonta los <details> al pulsar Expandir/Contraer todo. */}
          <div key={String(expandAll)} className="min-w-0 space-y-4 p-4">
            {isLoading && <EmptyData text="Cargando…" />}
            {error && <EmptyData text={`No se pudo cargar la trazabilidad: ${error}`} />}
            {!isLoading && !error && turns.length === 0 && (
              <EmptyData text="Todavía no hay actividad registrada para esta conversación." />
            )}

            {turns.map((turn, index) => {
              const toolErrors = turn.tools.filter((tool) => tool.status === 'error').length
              const llmRows: Array<[string, string]> = turn.llm
                ? [
                  ['Proveedor', turn.llm.provider || '—'],
                  ['Modelo', turn.llm.model || '—'],
                  ['Tokens de entrada', turn.llm.input_tokens?.toLocaleString('es-ES') ?? '—'],
                  ['Tokens de salida', turn.llm.output_tokens?.toLocaleString('es-ES') ?? '—'],
                  ['Tokens totales', turn.llm.total_tokens?.toLocaleString('es-ES') ?? '—'],
                  ['Coste estimado', typeof turn.llm.cost_usd_estimate === 'number' ? `${turn.llm.cost_usd_estimate.toFixed(7)} USD` : '—'],
                  ['Latencia', turn.llm.latency_ms != null ? `${turn.llm.latency_ms} ms` : '—'],
                  ['Estado de uso', turn.llm.usage_status || '—'],
                  ['Fuente de precio', turn.llm.pricing_source || '—'],
                ]
                : []

              return (
                <section
                  key={`${turn.trace_id}-${turn.turn_id}-${turn.ts}`}
                  className="min-w-0 overflow-hidden rounded-lg border bg-card"
                >
                  {/* Cabecera del turno */}
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-border bg-muted/30 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                        {index + 1}
                      </span>
                      <h3 className="truncate text-sm font-semibold">Turno {index + 1}</h3>
                      {toolErrors > 0 ? (
                        <span className="shrink-0 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                          {toolErrors} {toolErrors === 1 ? 'error' : 'errores'}
                        </span>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {formatTurnTimestamp(turn.ts)}
                    </span>
                  </div>

                  <div className="min-w-0 space-y-2 p-3">
                    {/* Conversación del turno */}
                    <div className="min-w-0 space-y-2">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <MessageSquare className="h-3 w-3 shrink-0" />
                        Conversación ({turn.messages.length})
                      </p>
                      {turn.messages.length > 0 ? (
                        turn.messages.map((message, messageIndex) => (
                          <div
                            key={`${turn.turn_id}-${message.role}-${messageIndex}`}
                            className={cn('flex min-w-0', message.role === 'user' ? 'justify-start' : 'justify-end')}
                          >
                            <div
                              className={cn(
                                'min-w-0 max-w-[88%] rounded-lg border px-3 py-2',
                                message.role === 'user'
                                  ? 'rounded-bl-none border-border bg-muted/60'
                                  : 'rounded-br-none border-primary/20 bg-primary/10',
                              )}
                            >
                              <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                {message.role === 'user' ? 'Cliente' : 'IA'}
                              </p>
                              <p className="whitespace-pre-wrap break-words text-xs leading-relaxed">{message.text}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <EmptyData text="Sin mensajes capturados en este turno." />
                      )}
                    </div>

                    {/* Herramientas consultadas */}
                    <div className="min-w-0 space-y-2">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <Wrench className="h-3 w-3 shrink-0" />
                        Herramientas consultadas ({turn.tools.length})
                      </p>
                      {turn.tools.length === 0 ? (
                        <EmptyData text="La IA respondió sin consultar ninguna herramienta." />
                      ) : (
                        turn.tools.map((tool) => (
                          <details
                            key={`${turn.turn_id}-${tool.id}`}
                            open={expandAll}
                            className="group min-w-0 overflow-hidden rounded-lg border"
                          >
                            <summary className="min-w-0 cursor-pointer list-none bg-muted/30 px-3 py-2">
                              <div className="flex min-w-0 items-center gap-2">
                                <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
                                <span className="min-w-0 break-all font-mono text-xs font-semibold">{tool.name}</span>
                                <StatusPill status={tool.status} />
                                <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground">
                                  {tool.latency_ms ?? '—'} ms
                                </span>
                              </div>
                              {tool.requestMethod || tool.requestUrl ? (
                                <p className="mt-1 min-w-0 break-all pl-5 font-mono text-[11px] text-muted-foreground">
                                  <span className="font-semibold text-foreground">{tool.requestMethod || '—'}</span>{' '}
                                  {tool.requestUrl || '—'}
                                </p>
                              ) : null}
                            </summary>

                            <div className="min-w-0 space-y-2 border-t border-border p-3">
                              {tool.toolCallId ? <IdRow label="tool_call_id" value={tool.toolCallId} /> : null}
                              {hasVisibleData(tool.requestHeaders) && (
                                <DataBlock title="Encabezados de solicitud" data={tool.requestHeaders} />
                              )}
                              {hasVisibleData(tool.requestQueryParams) && (
                                <DataBlock title="Parámetros de query" data={tool.requestQueryParams} />
                              )}
                              {hasVisibleData(tool.requestBody) && (
                                <DataBlock title="Cuerpo de la solicitud" data={tool.requestBody} />
                              )}
                              {hasVisibleData(tool.extractedParams) && (
                                <DataBlock title="Parámetros extraídos por la IA" data={tool.extractedParams} />
                              )}
                              {hasVisibleData(tool.response) && (
                                <DataBlock title="Respuesta" data={tool.response} />
                              )}
                              {hasVisibleData(tool.error) && (
                                <DataBlock title="Mensaje de error" data={tool.error} tone="danger" />
                              )}
                            </div>
                          </details>
                        ))
                      )}
                    </div>

                    {/* Modelo de lenguaje */}
                    {turn.llm && (
                      <div className="min-w-0 rounded-lg border p-3">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Modelo de lenguaje
                        </p>
                        <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2 2xl:grid-cols-3">
                          {llmRows.map(([label, value]) => (
                            <div key={label} className="flex min-w-0 items-baseline justify-between gap-2 border-b border-border pb-1">
                              <dt className="shrink-0 text-[11px] text-muted-foreground">{label}</dt>
                              <dd className="min-w-0 break-all text-right text-[11px] font-medium tabular-nums">{value}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}

                    {/* Identificadores del turno */}
                    <details className="group min-w-0 rounded-lg border bg-muted/20 px-3 py-2">
                      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                        <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" />
                        Identificadores del turno
                      </summary>
                      <div className="mt-2 min-w-0 space-y-1.5">
                        <IdRow label="turn_id" value={turn.turn_id} />
                        {turn.trace_id ? <IdRow label="trace_id" value={turn.trace_id} /> : null}
                      </div>
                    </details>
                  </div>
                </section>
              )
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
